import { useCallback } from 'react';

import type { MutationFunction, UseMutationOptions } from 'react-query';

import { useMutation } from 'react-query';

import { showErrorToast } from 'components-new/toast/toast';

import type { ErrorType } from 'state/custom-instance';
import { customInstance } from 'state/custom-instance';

import client from 'state/remote/client';

import type { Freelancer, FreelancerBody } from 'state/__generated__/tadaahAPI';

import { useGetFreelancerDetails } from './use-get-freelancer-details';

const apiFreelancerPartialUpdate = (freelancerBody: FreelancerBody) => {
  return customInstance<Freelancer>({
    data: freelancerBody,
    headers: { 'Content-Type': 'multipart/form-data' },
    method: 'patch',
    url: `/api/freelancer/`,
  });
};

const useApiFreelancerPartialUpdateCustom = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof apiFreelancerPartialUpdate>>,
    TError,
    { data: FreelancerBody },
    TContext
  >;
}) => {
  const { mutation: mutationOptions } = options ?? {};

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof apiFreelancerPartialUpdate>>,
    { data: FreelancerBody }
  > = (props) => {
    const { data } = props ?? {};

    return apiFreelancerPartialUpdate(data);
  };

  return useMutation<
    Awaited<ReturnType<typeof apiFreelancerPartialUpdate>>,
    TError,
    { data: FreelancerBody },
    TContext
  >(mutationFn, mutationOptions);
};

type UpdateFreelancerProfilePictureType = {
  picture: File | string | undefined;
};

interface UseUpdateFreelancerProfilePictureParams {
  onPictureIsSuccessCallback?: () => void;
  onPictureErrorCallback?: (error: ErrorType<unknown> | null) => void;
}

type UseUpdateFreelancerProfilePictureHandler = {
  currentPicture: string | undefined;
  initialValues: UpdateFreelancerProfilePictureType;

  isLoading: boolean;

  updateFreelancerPicture: (values: UpdateFreelancerProfilePictureType) => void;
};

const useUpdateFreelancerProfilePicture = ({
  onPictureIsSuccessCallback,
  onPictureErrorCallback,
}: UseUpdateFreelancerProfilePictureParams): UseUpdateFreelancerProfilePictureHandler => {
  const { data } = useGetFreelancerDetails();

  const { mutateAsync, isLoading } = useApiFreelancerPartialUpdateCustom({
    mutation: {
      mutationKey: 'update-freelancer-profile-picture',

      onError: (error) => {
        onPictureErrorCallback?.(error);
      },

      onSuccess: () => {
        onPictureIsSuccessCallback?.();
        const queriesToInvalidate = [
          'freelancer-details',
          'freelancer-signup-status',
        ];
        queriesToInvalidate.forEach((query) => {
          client.invalidateQueries(query);
        });
      },
    },
  });

  const updateFreelancerPicture = useCallback(
    (values: UpdateFreelancerProfilePictureType) => {
      const formData = new FormData();
      if (values.picture && values.picture !== data?.picture) {
        if (
          values.picture instanceof File &&
          values.picture.size > 1024 * 1024 * 4 // nginx config doesn't allow uploads more than 5 MB
        ) {
          showErrorToast({
            description: 'Image may not be larger than 4MB',
            title: 'Image too big',
          });
          throw new Error('image cannot be larger than 4MB');
        }
        formData.append('picture', values.picture);

        mutateAsync({
          data: formData as unknown as Freelancer,
        }).then((response) => {
          if (response.status !== 200) throw new Error('Something went wrong');
        });
      }
    },
    [data?.picture, mutateAsync],
  );

  const initialValues = {
    picture: data?.picture,
  };

  return {
    currentPicture: data?.picture,
    initialValues,
    isLoading,
    updateFreelancerPicture,
  };
};

export default useUpdateFreelancerProfilePicture;
